import { Controller } from "@hotwired/stimulus"
import { PushSubscriptionManager } from "../lib/push_subscription_manager"

// Manages push notification subscription lifecycle and syncing
// Mount this once in a permanent element:
// <div data-controller="webpush" data-turbo-permanent></div>
export default class extends Controller {
  static values = { 
    vapidKey: String,
    endpoint: String
  }
  static targets = ["enableButton"]

  connect() {
    console.debug('[WebPush] Controller connected')
    
    // Initialize push manager with VAPID key from data attribute
    this.initializePushManager()
    
    // Initial sync attempt when controller connects
    this.sync()
    
    // Update button visibility based on notification status
    this.updateButtonVisibility()
    
    // Re-sync on Turbo navigation (auth state may have changed)
    this._onTurboLoad = () => this.sync()
    
    // Re-sync when tab becomes visible (handles auth expiry, network reconnection)
    this._onVisibility = () => {
      if (document.visibilityState === 'visible') {
        this.sync()
      }
    }
    
    // Listen for messages from service worker
    this._onServiceWorkerMessage = (event) => this.handleServiceWorkerMessage(event)
    
    this.addEventListeners()
  }

  disconnect() {
    console.debug('[WebPush] Controller disconnected')
    this.removeEventListeners()
  }

  // Initialize push manager if not already done
  async initializePushManager() {
    if (window.vptxpPushManager && window.vptxpPushManager.constructor.name === 'PushSubscriptionManager') {
      console.debug('[WebPush] Push manager already exists')
      return
    }

    if (!this.vapidKeyValue) {
      console.warn('[WebPush] No VAPID key provided')
      return
    }

    try {
      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready
      
      // Create the manager
      const pushManager = new PushSubscriptionManager({
        vapidKey: this.vapidKeyValue,
        endpoint: this.endpointValue
      })
      
      await pushManager.initialize(registration)
      window.vptxpPushManager = pushManager
      
      console.debug('[WebPush] Push manager initialized')
    } catch (error) {
      console.error('[WebPush] Failed to initialize push manager:', error)
    }
  }

  // Public action: Enable notifications (for buttons with data-action="click->webpush#enable")
  enable() {
    console.debug('[WebPush] Manual enable triggered')
    if (window.vptxpPushManager) {
      window.vptxpPushManager.enable().then(() => {
        // Update button visibility after enabling
        this.updateButtonVisibility()
      })
    }
  }

  // Update button visibility based on notification permission
  updateButtonVisibility() {
    if (this.hasEnableButtonTarget) {
      const permission = Notification.permission
      if (permission === 'granted') {
        this.enableButtonTarget.classList.add('hidden')
      } else {
        this.enableButtonTarget.classList.remove('hidden')
      }
    }
  }

  // Public action: Sync subscription
  sync() {
    console.debug('[WebPush] Sync triggered')
    if (window.vptxpPushManager) {
      window.vptxpPushManager.sync()
    }
  }

  // Handle messages from service worker
  handleServiceWorkerMessage(event) {
    const { type, data } = event.data || {}
    
    switch (type) {
      case 'PUSH_RECEIVED':
        this.onPushReceived(data)
        break
      case 'NOTIFICATION_CLICKED':
        this.onNotificationClicked(data)
        break
      default:
        console.debug('[WebPush] Unknown service worker message:', type)
    }
  }

  // Override these methods in your app for custom behavior
  onPushReceived(data) {
    console.debug('[WebPush] Push received:', data)
    // Could update UI, show in-app notification, etc.
    this.dispatch('pushReceived', { detail: data })
  }

  onNotificationClicked(data) {
    console.debug('[WebPush] Notification clicked:', data)
    // Could update UI state, mark as read, etc.
    this.dispatch('notificationClicked', { detail: data })
  }

  // Private methods
  addEventListeners() {
    document.addEventListener('turbo:load', this._onTurboLoad)
    document.addEventListener('visibilitychange', this._onVisibility)
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this._onServiceWorkerMessage)
    }
  }

  removeEventListeners() {
    document.removeEventListener('turbo:load', this._onTurboLoad)
    document.removeEventListener('visibilitychange', this._onVisibility)
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this._onServiceWorkerMessage)
    }
  }
}